import { useAtom } from 'jotai'
import { BasicInput } from '../../../../../components/BasicInput'
import { Button } from '../../../../../components/ui/button'
import { currentStep } from '../../../../../../app/store'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { formData } from '../useNewWithdrawModal'
import { BasicSelect } from '../../../../../components/BasicSelect'
import { states } from '../../../../../../app/utils/states'
import { zipCodeMask } from '@/app/utils/zipCodeFormater'
import { cepService } from '@/app/services/cepService'

export const addressFormSchema = z.object({
  zipCode: z.string().min(8, '* obrigatório'),
  neighborhood: z.string().min(4, '* obrigatório'),
  city: z.string().min(4, '* obrigatório'),
  state: z.string({ required_error: '* obrigatório' }).min(2).max(2),
  street: z.string().min(5, '* obrigatório'),
  number: z.string().optional().or(z.literal('')),
  complement: z.string().optional(),
})

type addressformData = z.infer<typeof addressFormSchema>
interface AddressFormProps {
  visible?: boolean
}
export default function AddressForm({ visible }: AddressFormProps) {
  const [, setStep] = useAtom(currentStep)

  const addressForm = useForm<addressformData>({
    resolver: zodResolver(addressFormSchema),
  })

  const {
    handleSubmit,
    formState: { errors },
    reset,
  } = addressForm

  const { setValue, getValues } = useFormContext<formData>()

  const submitForm = (data: addressformData) => {
    setValue('address', data)
    setStep((state) => state + 1)
  }

  const handleCepRequest = async (cep: string) => {
  const response = await cepService(cep.replace(/\D/g, ''))

    if (response) {
  addressForm.setValue('zipCode', response.cep.replace(/\D/g, ''))
      addressForm.setValue('city', response.localidade)
      addressForm.setValue('complement', response.complemento)
      addressForm.setValue('neighborhood', response.bairro)
      addressForm.setValue('state', response.uf)
      addressForm.setValue('street', response.logradouro)
    }
  }

  useEffect(() => {
    const values = getValues()
    if (values && values.address) reset(values.address)
  }, [reset, getValues])

  return (
    <div className={visible ? 'flex flex-col gap-8 max-sm:gap-4' : 'hidden'}>
      <FormProvider {...addressForm}>
        <div className="grid grid-cols-4 gap-4 max-sm:grid-cols-1 max-sm:gap-3">
          <BasicInput
            name="zipCode"
            label="CEP"
            error={errors.zipCode?.message}
            onInput={zipCodeMask}
            maxLength={9}
            onBlur={(e) => handleCepRequest(e.target.value)}
          />
        </div>

        <div className="flex gap-4 max-sm:flex-col max-sm:gap-3">
          <div className="grow">
            <BasicInput
              name="street"
              label="Endereço"
              error={errors.street?.message}
            />
          </div>
          <div className="w-full max-w-24 max-sm:max-w-none">
            <BasicInput
              name="number"
              label="Número"
              error={errors.number?.message}
            />
          </div>
        </div>

        <div>
          <div className="grid grid-cols-4 gap-4 max-sm:grid-cols-1 max-sm:gap-3 max-md:grid-cols-2">
            <BasicInput
              name="complement"
              label="Complemento"
              error={errors.complement?.message}
            />

            <BasicInput
              name="neighborhood"
              label="Bairro"
              error={errors.neighborhood?.message}
            />

            <BasicInput
              name="city"
              label="Cidade"
              error={errors.city?.message}
            />

            <BasicSelect
              label="UF"
              name="state"
              options={states}
              placeholder="Selecione o seu estado"
              error={errors.state?.message}
            />
          </div>
        </div>
      </FormProvider>
      <div className="flex justify-end gap-5 max-sm:flex-col max-sm:gap-3">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02 max-sm:order-2"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white max-sm:order-1"
          onClick={handleSubmit(submitForm)}
        >
          Continuar
        </Button>
      </div>
    </div>
  )
}
